import { Box, Chip, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { CategoryDetail } from "../../types/categoryDetail";
import { codeWords, dateRange } from "../Detail/format";
import {
  DEFAULT_EXPLORE_STATE,
  encodeExploreState,
} from "../Explore/exploreState";
import OpenInValueChainLink from "../KindDetail/OpenInValueChainLink";
import PathTrail from "./PathTrail";
import SourceCountBlock, { FirstAndLatest, Mono } from "./SourceCountBlock";

// The rank of a curated row that is a kind of the value chain
const KIND_RANK = "type";

interface CategoryHeadProps {
  category: CategoryDetail;
  // Whether the head offers the step into the value chain; a view that
  // adds its own entry point to the chain leaves it out
  valueChainLink?: boolean;
}

const chipSx = { height: 24, fontSize: "0.75rem" } as const;

// What a class reads when no label names it
export const UNLABELLED_CATEGORY = "Unlabelled category";

/**
 * The address of Explore with the class picked and, where a specification
 * key is given, a range on that key without bounds: every product of the
 * class that states the specification.
 */
export const exploreAddress = (
  categoryId: string,
  specificationKey?: string,
): string =>
  `/explore?${encodeExploreState({
    ...DEFAULT_EXPLORE_STATE,
    category: [categoryId],
    numeric:
      specificationKey === undefined
        ? []
        : [{ key: specificationKey, minimum: null, maximum: null }],
  }).toString()}`;

// The chip of a curated class: "curated · group", or "curated" without a rank
const curatedChip = (category: CategoryDetail): string | null =>
  category.curated
    ? ["curated", category.rank ? codeWords(category.rank) : null]
        .filter(Boolean)
        .join(" · ")
    : null;

/**
 * The head of the category view: the path of the class, its label, its
 * other labels, the chips for curation, domain and nature, and how much
 * evidence and how many products there are, with the timeline of the
 * sources.
 */
const CategoryHead: React.FC<CategoryHeadProps> = ({
  category,
  valueChainLink = true,
}) => {
  // The path ends with the class itself, which the trail ends with unlinked
  const ancestors = category.path.filter((entry) => entry.id !== category.id);
  const label = category.label ?? UNLABELLED_CATEGORY;
  const curated = curatedChip(category);
  const kinds = [category.domain, category.nature].filter(
    (kind): kind is string => Boolean(kind),
  );

  const sourceDates = useMemo(
    () => category.sources.items.map((source) => source.date),
    [category.sources.items],
  );
  const range = useMemo(() => dateRange(sourceDates), [sourceDates]);
  const products = category.products.total;

  return (
    <Box
      component="header"
      sx={{
        px: 3,
        pt: 3,
        pb: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box>
        {ancestors.length > 0 && <PathTrail above={ancestors} own={label} />}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            overflowWrap: "anywhere",
            color: category.label ? "text.primary" : "text.secondary",
            fontStyle: category.label ? "normal" : "italic",
          }}
        >
          {label}
        </Typography>
        {category.other_labels.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Other labels:{" "}
            <Box component="span" sx={{ color: "text.primary" }}>
              {category.other_labels.join(", ")}
            </Box>
          </Typography>
        )}
      </Box>

      {(curated || kinds.length > 0) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {curated && <Chip label={curated} variant="outlined" sx={chipSx} />}
          {kinds.map((kind) => (
            <Chip key={kind} label={codeWords(kind)} sx={chipSx} />
          ))}
        </Box>
      )}

      <SourceCountBlock
        count={category.source_count}
        dates={sourceDates}
        after={
          <>
            {" · "}
            <Mono>{products}</Mono>{" "}
            {products === 1 ? "product" : "products"} including narrower
            classes
          </>
        }
        footnote={
          range && <FirstAndLatest first={range.first} latest={range.latest} />
        }
        action={
          valueChainLink && (
            <OpenInValueChainLink
              root={{
                type: category.rank === KIND_RANK ? "kind" : "class",
                id: category.id,
              }}
            />
          )
        }
      />
    </Box>
  );
};

export default CategoryHead;
